import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { Badge, Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import React from 'react'
import { ExclusionAreaGeoJSON, RefData } from '../../../../types/apiModels'
import { COLORS } from '../../../../utils/constants'
import { formattedHectaresArea, getExclusionAreaTypeColor } from '../../utils'

interface ExclusionAreaItemProps {
  exclusionArea: ExclusionAreaGeoJSON
  exclusionAreaTypes: RefData[] | null
  isDisabled?: boolean
  onEdit: () => void
  onDelete: () => void
  onComment?: () => void
  unresolvedCount?: number
}

export const ExclusionAreaItem: React.FC<ExclusionAreaItemProps> = ({
  exclusionArea,
  exclusionAreaTypes,
  isDisabled = false,
  onEdit,
  onDelete,
  onComment,
  unresolvedCount = 0,
}) => {
  const { properties } = exclusionArea

  const exclusionAreaType = exclusionAreaTypes?.find(
    type => type.id === properties.exclusionAreaTypeId,
  )

  const chipColor = getExclusionAreaTypeColor(String(properties.exclusionAreaTypeId))

  const grazingText = properties.hasGrazingManagement ? 'Con pastoreo' : 'Sin pastoreo'
  const grazingColor = properties.hasGrazingManagement ? COLORS.GREEN : COLORS.GRAY

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ alignItems: 'center', py: 2, width: '100%', justifyContent: 'space-between' }}
    >
      <Box sx={{ minWidth: 130, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {exclusionAreaType && (
          <Chip
            label={exclusionAreaType.es_AR}
            size="small"
            sx={{
              alignSelf: 'flex-start',
              backgroundColor: chipColor,
              color: '#fff',
              fontWeight: 500,
              width: 130,
            }}
          />
        )}

        <Typography
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: 130,
          }}
          title={properties.otherName}
          variant="body2"
        >
          {properties.otherName}
        </Typography>
      </Box>

      <Typography
        sx={{
          color: grazingColor,
          fontWeight: 500,
        }}
        variant="body2"
      >
        {grazingText}
      </Typography>

      <Typography color="text.secondary" variant="body2">
        {formattedHectaresArea(exclusionArea)}
      </Typography>

      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {exclusionArea.properties.id && onComment && (
          <IconButton
            aria-label="ver observaciones"
            color="default"
            size="small"
            onClick={onComment}
          >
            <Tooltip title="Ver observaciones de auditoría">
              <Badge
                badgeContent={unresolvedCount}
                color="warning"
                invisible={unresolvedCount === 0}
              >
                <ChatBubbleOutlineIcon />
              </Badge>
            </Tooltip>
          </IconButton>
        )}

        <IconButton
          aria-label="edit"
          color="primary"
          disabled={isDisabled}
          size="small"
          onClick={onEdit}
        >
          <Tooltip title="Editar la capa completa con sus geometrías">
            <EditIcon />
          </Tooltip>
        </IconButton>

        <IconButton
          aria-label="delete"
          color="error"
          disabled={isDisabled}
          size="small"
          onClick={onDelete}
        >
          <Tooltip title="Eliminar la capa completa con sus geometrías">
            <DeleteIcon />
          </Tooltip>
        </IconButton>
      </Box>
    </Stack>
  )
}
